<script setup>
import { computed, watch } from "vue";
import { useField } from "vee-validate";
import * as yup from "yup";
import DatePicker from "primevue/datepicker";
import {
  getDisabledWeekdays,
  getDisabledDatesFromUnavailable,
} from "@app/helpers";

const props = defineProps({
  modelValue: {
    type: [Date, String, Array, null], // Can be single date (Date/String) or range (Array of Dates/Strings)
    default: null,
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  required: {
    type: Boolean,
    default: false,
  },
  placeholder: String,
  minDate: Date,
  maxDate: Date,
  dateFormat: {
    type: String,
    default: "dd/mm/yy",
  },
  description: String,
  manualInput: {
    type: Boolean,
    default: false,
  },
  showRequired: {
    type: Boolean,
    default: true,
  },
  errorPosition: {
    type: String,
    default: "static",
  },
  selectionMode: {
    type: String,
    default: "single", // "single" or "range"
  },
  hideOnRangeSelection: {
    type: Boolean,
    default: true, // PrimeVue's default for range
  },
  openingTimes: {
    type: Object,
    default: () => ({}),
  },
  unavailableDays: {
    type: Array,
    default: () => [],
  },
  enableClinicFiltering: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "dateSelect"]);

// Helper to convert a single item to a Date object or null
const toDateObject = (item) => {
  if (item === null || item === undefined) return null;
  // If it's already a Date object and valid, return it
  if (item instanceof Date && !isNaN(item.getTime())) return item;
  // Try to parse it as a new Date
  const date = new Date(item);
  // Return the Date object if valid, otherwise null
  return isNaN(date.getTime()) ? null : date;
};

// Computed property to always have Date objects from modelValue
const internalDateValue = computed(() => {
  if (props.selectionMode === "range") {
    if (Array.isArray(props.modelValue)) {
      return props.modelValue.map(toDateObject);
    }
    // For range, if modelValue is not an array, default to [null, null] or handle as appropriate
    // PrimeVue's DatePicker expects an array for range mode.
    return props.modelValue === null ? null : [null, null];
  } else {
    return toDateObject(props.modelValue);
  }
});

const schema = computed(() => {
  let baseSchema;
  if (props.selectionMode === "range") {
    baseSchema = yup.array().of(yup.date().nullable());
    if (props.required) {
      baseSchema = baseSchema
        .min(2, `${props.label} requires a start and end date.`)
        .test(
          "has-both-dates",
          `${props.label} requires both start and end dates to be selected.`,
          (value) => {
            if (!value || value.length < 2) return false; // Must have two dates for a range
            return value[0] !== null && value[1] !== null; // Both dates must be non-null
          }
        )
        .required(`${props.label} is required.`);
    }
  } else {
    baseSchema = yup.date().nullable();
    if (props.required) {
      baseSchema = baseSchema
        .required(`${props.label} is required.`)
        .typeError(`${props.label} must be a valid date.`);
    } else {
      baseSchema = baseSchema.typeError(
        `${props.label} must be a valid date if provided.`
      );
    }
  }
  return baseSchema;
});

const { value, errorMessage, validate, setValue } = useField(
  () => props.name,
  schema,
  {
    initialValue: internalDateValue.value, // Use the computed Date object(s)
    validateOnValueUpdate: false, // Important for date pickers
  }
);

// Watch for changes from the DatePicker (vee-validate's 'value')
// This value should be Date objects from PrimeVue DatePicker
watch(value, (newValue) => {
  emit("update:modelValue", newValue);
});

// Watch for changes from the parent modelValue, processed by internalDateValue
watch(
  internalDateValue,
  (newDateObjects) => {
    let areEquivalent = false;
    if (props.selectionMode === "range") {
      if (
        Array.isArray(newDateObjects) &&
        Array.isArray(value.value) &&
        newDateObjects.length === value.value.length
      ) {
        areEquivalent = newDateObjects.every((date, i) => {
          const valDate = value.value[i];
          if (date === null && valDate === null) return true;
          if (date instanceof Date && valDate instanceof Date)
            return date.getTime() === valDate.getTime();
          return false;
        });
      } else if (newDateObjects === null && value.value === null) {
        // Both are null (e.g. if range is cleared)
        areEquivalent = true;
      }
    } else {
      // single mode
      if (newDateObjects === null && value.value === null) {
        areEquivalent = true;
      } else if (
        newDateObjects instanceof Date &&
        value.value instanceof Date
      ) {
        areEquivalent = newDateObjects.getTime() === value.value.getTime();
      }
    }

    if (!areEquivalent) {
      setValue(newDateObjects);
    }
  },
  { deep: true } // modelValue can be an array
);
// Computed property for disabled weekdays based on clinic opening times
const disabledDays = computed(() => {
  if (
    !props.enableClinicFiltering ||
    !props.openingTimes ||
    Object.keys(props.openingTimes).length === 0
  ) {
    return [];
  }
  return getDisabledWeekdays(props.openingTimes);
});

// Computed property for disabled dates based on unavailable days
const disabledDates = computed(() => {
  if (!props.enableClinicFiltering || !Array.isArray(props.unavailableDays)) {
    return [];
  }
  return getDisabledDatesFromUnavailable(props.unavailableDays);
});

// Watch for changes in openingTimes and clear date if it becomes invalid
watch(
  () => props.openingTimes,
  (newOpeningTimes) => {
    if (props.enableClinicFiltering && newOpeningTimes && value.value) {
      const newDisabledDays = getDisabledWeekdays(newOpeningTimes);

      // Check if current selected date is now disabled
      if (value.value instanceof Date) {
        const dayOfWeek = value.value.getDay(); // 0=Sunday, 1=Monday, etc.

        if (newDisabledDays.includes(dayOfWeek)) {
          setValue(null);
          console.log(
            "FieldDatePicker: Cleared date selection due to opening times change"
          );
        }
      }
    }
  },
  { deep: true }
);

const handleDateSelect = () => {
  emit("dateSelect", value.value);
};
</script>

<template>
  <fieldset class="frm-field">
    <div class="mb-10" :class="{ 'flex justify-between': $slots.label }">
      <label :for="name" class="block">
        {{ label }}
        <span v-if="required && showRequired" class="text-red-500">*</span>
      </label>
      <slot name="label"></slot>
    </div>

    <div class="px-0 frm-input">
      <DatePicker
        :id="name"
        :name="name"
        v-model="value"
        :placeholder="placeholder"
        :selectionMode="selectionMode"
        :hideOnRangeSelection="hideOnRangeSelection"
        :manualInput="manualInput"
        :minDate="minDate"
        :maxDate="maxDate"
        :dateFormat="dateFormat"
        :disabledDays="disabledDays"
        :disabledDates="disabledDates"
        @date-select="handleDateSelect"
        @hide="validate"
        class="w-full"
        :class="{ 'p-invalid': errorMessage }" />
    </div>

    <span
      v-if="errorMessage"
      class="block mt-10 text-14 text-error leading-none"
      :class="{ 'md:absolute': errorPosition === 'absolute' }">
      {{ errorMessage }}
    </span>
    <span v-if="description" class="frm-desc">
      {{ description }}
    </span>
  </fieldset>
</template>
